import React, { useState } from 'react';
import { X, Video, Clock, Award, Star, Zap, Trophy } from 'lucide-react';
// eslint-disable-next-line
import { authenticatedFetch } from '../../utils/api';
import styles from './Notifications.module.css';
import { useAuth } from '../../context/AuthContext';
import VideoCall from '../VideoCall/VideoCall';

const NotificationsPanel = ({ notifications, onClose, onUpdate, isLoading, isVisible }) => {
    const [showVideoCall, setShowVideoCall] = useState(false);
    const [activeVideoRoom, setActiveVideoRoom] = useState(null);
    // eslint-disable-next-line
    const [activeMeetingWith, setActiveMeetingWith] = useState(null);
    const { user } = useAuth();

    const getSessionStatus = (startsAt, expiresAt) => {
        if (!startsAt || !expiresAt) return 'invalid';

        const now = new Date();
        const startTime = new Date(startsAt);
        const endTime = new Date(expiresAt);

        if (now > endTime) return 'expired';
        if (now < startTime) return 'upcoming';
        return 'active';
    };

    const formatSessionTime = (startsAt, expiresAt) => {
        if (!startsAt || !expiresAt) return '';

        const startTime = new Date(startsAt);
        const endTime = new Date(expiresAt);
        const now = new Date();

        // Format times
        const timeFormatter = new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        });

        // For upcoming sessions
        if (now < startTime) {
            const diffMs = startTime - now;
            const diffMins = Math.round(diffMs / 60000);

            if (diffMins < 60) {
                return `Starts in ${diffMins} minute${diffMins === 1 ? '' : 's'}`;
            }
            return `Starts at ${timeFormatter.format(startTime)}`;
        }

        // For active sessions
        if (now >= startTime && now <= endTime) {
            const diffMs = endTime - now;
            const diffMins = Math.round(diffMs / 60000);
            return `${diffMins} minute${diffMins === 1 ? '' : 's'} remaining`;
        }

        return 'Session has ended';
    };

    const handleJoinCall = (roomUrl, meetingWith, notification) => {
        if (!roomUrl) {
            console.error('No room URL provided');
            return;
        }
        // Determine if this is a coach notification by checking for userName
        const isCoach = !!notification.userName;
        // Add the role parameter to the room URL
        const urlWithRole = `${roomUrl}${roomUrl.includes('?') ? '&' : '?'}role=${isCoach ? 'coach' : 'user'}`;
        setActiveVideoRoom(urlWithRole);
        setActiveMeetingWith(meetingWith);
        setShowVideoCall(true);
    };

    // Get random icon for level up notification
    const getLevelUpIcon = () => {
        const icons = [
            <Trophy size={20} />,
            <Star size={20} />,
            <Award size={20} />,
            <Zap size={20} />
        ];
        return icons[Math.floor(Math.random() * icons.length)];
    };

    const renderNewBooking = (notification) => {
        const sessionStatus = getSessionStatus(notification.startsAt, notification.expiresAt);
        const timeMessage = formatSessionTime(notification.startsAt, notification.expiresAt);

        // Determine if this is a coach notification by checking for userName in the data
        const isCoachNotification = !!notification.userName;

        return (
            <div className={styles.notificationContent}>
                <p>
                    <strong>
                        {isCoachNotification ? notification.userName : notification.coachName}
                    </strong>
                    {isCoachNotification ? ' booked a coaching session' : ' confirmed your coaching session'}
                </p>

                {notification.message && (
                    <div className={styles.notificationMessage}>
                        <p>{notification.message}</p>
                    </div>
                )}

                {notification.timeSlot && (
                    <div className={styles.timeSlotInfo}>
                        <Clock size={16} />
                        <span>Scheduled for: {notification.timeSlot}</span>
                    </div>
                )}

                {notification.hourlyRate && !isCoachNotification && (
                    <p className={styles.rateInfo}>
                        Paid: ${notification.hourlyRate}/hour
                    </p>
                )}

                {notification.roomUrl && (
                    <>
                        <button
                            className={styles.joinCallButton}
                            onClick={() => handleJoinCall(
                                notification.roomUrl,
                                isCoachNotification ? notification.userName : notification.coachName,
                                notification
                            )}
                            disabled={sessionStatus !== 'active'}
                        >
                            {sessionStatus === 'active' ? (
                                <>
                                    <Video size={16} />
                                    Join Session
                                </>
                            ) : sessionStatus === 'upcoming' ? (
                                <>
                                    <Clock size={16} />
                                    Session Not Started
                                </>
                            ) : (
                                <>
                                    <X size={16} />
                                    Session Expired
                                </>
                            )}
                        </button>
                        <p className={`${styles.sessionNote} ${sessionStatus === 'expired' ? styles.expiredNote : ''}`}>
                            {timeMessage}
                        </p>
                    </>
                )}
            </div>
        );
    };

    const renderLevelUp = (notification) => {
        return (
            <div className={styles.levelUpNotification}>
                <div className={styles.levelUpHeader}>
                    <div className={styles.levelUpIcon}>
                        {getLevelUpIcon()}
                    </div>
                    {notification.title && <h4 className={styles.levelUpTitle}>{notification.title}</h4>}
                </div>
                <div className={styles.levelUpMessage}>
                    {notification.message}
                </div>

                <div className={styles.levelUpDetails}>
                    <div className={styles.levelBadge}>
                        <span>{notification.level}</span>
                    </div>
                    <div className={styles.xpCounter}>
                        <span>{notification.xp} XP</span>
                    </div>
                </div>

                <div className={styles.levelUpProgress}>
                    <div className={styles.levelUpProgressBar}></div>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className={`${styles.panel} ${isVisible ? styles.visible : ''}`}>
                <div className={styles.panelHeader}>
                    <h3>Notifications</h3>
                    <button onClick={onClose} className={styles.closeButton}>
                        <X size={16} />
                    </button>
                </div>
                <div className={styles.notificationsList}>
                    {notifications.length === 0 ? (
                        <p className={styles.noNotifications}>
                            {isLoading ? 'Loading notifications...' : "No notifications yet"}
                        </p>
                    ) : (
                        notifications.map((notification) => (
                            <div
                                key={notification._id}
                                className={`${styles.notification} ${!notification.read ? styles.unread : ''}`}
                            >
                                {notification.type === 'new_booking' && renderNewBooking(notification)}
                                {notification.type === 'level_up' && renderLevelUp(notification)}
                                <span className={styles.time}>
                                    {new Date(notification.timestamp).toLocaleString()}
                                </span>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {showVideoCall && activeVideoRoom && (
                <VideoCall
                    roomUrl={activeVideoRoom}
                    participantName={user?.fullName || 'User'}
                    onClose={() => {
                        setShowVideoCall(false);
                        setActiveVideoRoom(null);
                        setActiveMeetingWith(null);
                    }}
                />
            )}
        </>
    );
};

export default NotificationsPanel;